import { useState, type FormEvent, type ReactNode } from 'react';
import { Save } from 'lucide-react';
import { BACKUP_COPIES_MAX } from '@cp/shared';
import { Banner, Button, Dialog, Field, Input } from '@/components/ui';
import { useSaveBackupSettings } from '@/hooks/useBackups';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';

/**
 * The owner's cap on copies per site (ADR-0056). Each new copy, once
 * verified, takes the place of the oldest; lowering the cap removes the copies
 * beyond it within minutes, which is why the dialog says so before saving.
 */
export function BackupSettingsDialog({ current, onClose }: { readonly current: number; readonly onClose: () => void }): ReactNode {
  const [copies, setCopies] = useState(String(current));
  const save = useSaveBackupSettings({
    onSuccess: (saved) => {
      toast.success('Сохранено', `Копий на сайт: ${saved.copiesPerSite}`);
      onClose();
    },
  });
  const value = Number(copies);
  const valid = Number.isInteger(value) && value >= 1 && value <= BACKUP_COPIES_MAX;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (valid) save.mutate({ copiesPerSite: value });
  };

  return (
    <Dialog
      open
      title="Копий на сайт"
      description="Новая копия после проверки заменяет самую старую и занимает её номер"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" icon={Save} busy={save.isPending} disabled={!valid} type="submit" form="backup-settings">
            Сохранить
          </Button>
        </>
      }
    >
      <form id="backup-settings" className="stack" onSubmit={submit}>
        <Field label="Хранить копий" htmlFor="backup-copies" hint={`От 1 до ${BACKUP_COPIES_MAX}. Копия перед восстановлением или переносом хранится неделю сверх этого`} {...(copies && !valid ? { error: `Целое число от 1 до ${BACKUP_COPIES_MAX}` } : {})}>
          <Input id="backup-copies" type="number" min={1} max={BACKUP_COPIES_MAX} value={copies} onChange={(event) => setCopies(event.target.value)} />
        </Field>
        {valid && value < current ? (
          <Banner tone="warning">Лишние копии — {current - value} на сайт — удалятся в ближайшие минуты, начиная с самых старых.</Banner>
        ) : null}
        {save.isError ? <p className="field-error">{errorText(save.error)}</p> : null}
      </form>
    </Dialog>
  );
}
